import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { UserRound, X } from 'lucide-react';
import { toast } from 'react-toastify';

import {
  useFetchCallProfile,
  useSaveCallProfile,
} from '../../../services/onboardingBoard';

// "My bio" — the specialist's own [bio] script token (users.userCadenceBio).
// Opened from the board's settings gear, and auto-opened when a send is
// blocked by an unfilled [bio]. (The Calendly link was retired 2026-08-09:
// leads never self-book with Onboarding Support.)

// Hard cap mirrors CADENCE_BIO_MAX_LENGTH server-side: the bio lands inside
// outreach emails — 2-3 short sentences, never a wall of text.
const BIO_MAX = 280;

const CallProfileEditor = ({ show, onHide }) => {
  const queryClient = useQueryClient();
  const profileQ = useFetchCallProfile({ enabled: show });
  const profile = profileQ.data?.profile;

  const [bio, setBio] = useState('');
  useEffect(() => {
    if (profile) setBio(profile.userCadenceBio || '');
  }, [profile]);

  const saveMut = useSaveCallProfile(
    () => {
      // Script previews/compose prefills read this query — refresh so the
      // new bio renders immediately.
      queryClient.invalidateQueries({ queryKey: ['call-profile'] });
      toast.success('Bio saved.');
      onHide();
    },
    {
      onError: (err) =>
        toast.error(err?.response?.data?.msg || 'That didn’t go through.'),
    },
  );

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1080] flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-slate-900/30 dark:bg-black/60"
        aria-hidden
        onClick={onHide}
      />
      <div className="relative m-4 w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10">
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
            <UserRound size={15} className="text-slate-400" />
            My bio
          </div>
          <button
            type="button"
            onClick={onHide}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
          >
            <X size={14} />
          </button>
        </div>
        <div className="mb-3 text-xs text-slate-400">
          Fills the <span className="font-mono">[bio]</span> token in your
          cadence notifications. Nothing sends with the token unfilled.
        </div>

        <label className="mb-1 block">
          <span className="mb-1 flex items-center justify-between text-[11px] font-semibold text-slate-500 dark:text-slate-400">
            Short bio (2-3 sentences)
            <span
              className={`font-normal ${bio.length > BIO_MAX ? 'text-rose-500' : 'text-slate-400'}`}
            >
              {bio.length}/{BIO_MAX}
            </span>
          </span>
          <textarea
            value={bio}
            rows={4}
            placeholder="A little bit about your VA rating background…"
            onChange={(e) => setBio(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-transparent px-2.5 py-1.5 text-xs leading-relaxed text-slate-700 focus:border-slate-400 focus:outline-none dark:border-white/10 dark:text-slate-200 dark:focus:border-white/30"
          />
        </label>

        {bio ? (
          <div className="mb-3 rounded-lg bg-slate-50 p-2.5 dark:bg-white/5">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-300 dark:text-slate-600">
              How it lands in the email
            </div>
            <div className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
              A little bit about myself. {bio}
            </div>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onHide}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saveMut.isLoading || bio.length > BIO_MAX}
            onClick={() => saveMut.mutate({ userCadenceBio: bio })}
            className="rounded-lg bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-white"
          >
            {saveMut.isLoading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CallProfileEditor;
